import { useEffect } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import {
  Bold,
  FileText,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Paperclip,
  Redo2,
  Strikethrough,
  Underline,
  Undo2,
} from "lucide-react";

import { cn } from "@/lib/utils";

interface Props {
  value: string;
  onChange: (htmlValue: string, plainText: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  /** Mail-leeskolom (02-08): zet een paperclip-knop in de werkbalk —
   *  het losse sleepvak is vervallen, bijlagen gaan via deze knop of
   *  door ze op de editor te slepen. */
  onBijlage?: () => void;
  /** Mailronde 06-08: zet een sjabloon-knop in de werkbalk die de
   *  sjabloonkiezer opent. */
  onSjabloon?: () => void;
}

/**
 * Rich-text editor (TipTap) voor de reply-editor. Output is HTML
 * (voor Gmail) plus platte tekst (voor toon-check en fallback).
 *
 * Alleen basis-formatting: bold / italic / underline / strike, bullet
 * + numbered lists, links. Geen headings/colors om de mails clean te
 * houden — client-mail renderers zijn grillig met zware opmaak.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
  rows = 10,
  onBijlage,
  onSjabloon,
}: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // We gebruiken geen headings / code-blocks / blockquotes in
        // klantenservice-mails — te grillig over mail-clients.
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: "text-brand-cyan-dark underline",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
    ],
    content: value,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn(
          "tiptap prose prose-sm max-w-none focus:outline-none px-3 py-2.5",
          "text-[13.5px] leading-relaxed text-slate-900",
          "min-h-[160px]",
        ),
        style: `min-height: ${rows * 18}px`,
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML(), editor.getText());
    },
  });

  // Sync wanneer externe `value` verandert (bijv. AI-suggestie "Gebruik").
  // ``editor`` hoort in de deps (03-08): hij is er op de eerste render nog
  // niet, dus een waarde die vlak ná het mounten wordt gezet — zoals de
  // begroeting in de nieuwe-mail-dialoog — kwam anders nooit in beeld.
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-brand-cyan/40 focus-within:border-brand-cyan">
      <Toolbar
        editor={editor}
        disabled={disabled}
        onBijlage={onBijlage}
        onSjabloon={onSjabloon}
      />
      {/* Trengo-model (02-08): geen inner-scroll — het vak groeit mee
          met de inhoud, de kolom eromheen is de enige scroller. */}
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}

function Toolbar({
  editor,
  disabled,
  onBijlage,
  onSjabloon,
}: {
  editor: Editor;
  disabled?: boolean;
  onBijlage?: () => void;
  onSjabloon?: () => void;
}) {
  const ToolBtn = ({
    actief,
    onClick,
    label,
    icon: Icon,
  }: {
    actief: boolean;
    onClick: () => void;
    label: string;
    icon: typeof Bold;
  }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        "h-7 w-7 grid place-items-center rounded text-slate-600",
        "hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan",
        actief && "bg-slate-200 text-slate-900",
        disabled && "opacity-40 cursor-not-allowed",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <div className="flex items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
      <ToolBtn
        actief={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
        label="Bold (⌘B)"
        icon={Bold}
      />
      <ToolBtn
        actief={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        label="Italic (⌘I)"
        icon={Italic}
      />
      <ToolBtn
        actief={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline?.().run()}
        label="Underline"
        icon={Underline}
      />
      <ToolBtn
        actief={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        label="Doorstrepen"
        icon={Strikethrough}
      />
      <span className="h-4 w-px bg-slate-300 mx-1" />
      <ToolBtn
        actief={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        label="Opsomming"
        icon={List}
      />
      <ToolBtn
        actief={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        label="Genummerd"
        icon={ListOrdered}
      />
      <span className="h-4 w-px bg-slate-300 mx-1" />
      <ToolBtn
        actief={editor.isActive("link")}
        onClick={() => {
          const vorige = editor.getAttributes("link").href as string | undefined;
          const url = window.prompt("Link URL:", vorige ?? "https://");
          if (url === null) return;
          if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
          }
          editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();
        }}
        label="Link"
        icon={LinkIcon}
      />
      {onBijlage && (
        <>
          <span className="h-4 w-px bg-slate-300 mx-1" />
          <ToolBtn
            actief={false}
            onClick={onBijlage}
            label="Bijlage toevoegen"
            icon={Paperclip}
          />
        </>
      )}
      {onSjabloon && (
        <>
          {!onBijlage && <span className="h-4 w-px bg-slate-300 mx-1" />}
          <ToolBtn
            actief={false}
            onClick={onSjabloon}
            label="Sjabloon invoegen"
            icon={FileText}
          />
        </>
      )}
      <span className="flex-1" />
      <ToolBtn
        actief={false}
        onClick={() => editor.chain().focus().undo().run()}
        label="Ongedaan (⌘Z)"
        icon={Undo2}
      />
      <ToolBtn
        actief={false}
        onClick={() => editor.chain().focus().redo().run()}
        label="Opnieuw (⌘⇧Z)"
        icon={Redo2}
      />
    </div>
  );
}
